// Lista de cotizaciones con totales + status + link público.

import { quotesApi } from '../api';
import { useQuotes } from '../hooks/useQuotes';
import type { Quote, QuoteStatus } from '../types';

const STATUS_LABEL: Record<QuoteStatus, string> = {
  draft: 'Borrador',
  sent: 'Enviada',
  viewed: 'Vista',
  accepted: 'Aceptada',
  rejected: 'Rechazada',
  expired: 'Expirada',
};

interface Props {
  status?: QuoteStatus;
  onSelect?: (quote: Quote) => void;
}

export function QuoteList({ status, onSelect }: Props) {
  const { data, loading, error, refetch } = useQuotes({ status, limit: 50 });

  if (loading && !data) {
    return <div className="p-6 text-sm text-muted-foreground">Cargando cotizaciones…</div>;
  }
  if (error) {
    return (
      <div className="p-6 text-sm text-destructive">
        Error: {error} ·{' '}
        <button onClick={refetch} className="underline">Reintentar</button>
      </div>
    );
  }
  if (!data || data.items.length === 0) {
    return (
      <div className="p-6 text-sm text-muted-foreground">
        Sin cotizaciones todavía. Crea la primera desde el formulario.
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead>
          <tr className="text-left border-b">
            <th className="px-3 py-2">Cliente</th>
            <th className="px-3 py-2">Título</th>
            <th className="px-3 py-2">Status</th>
            <th className="px-3 py-2 text-right">Total</th>
            <th className="px-3 py-2">Vence</th>
            <th className="px-3 py-2">PDF</th>
            <th className="px-3 py-2">Link público</th>
          </tr>
        </thead>
        <tbody>
          {data.items.map((q) => (
            <tr
              key={q.id}
              className="border-b hover:bg-muted cursor-pointer"
              onClick={() => onSelect?.(q)}
            >
              <td className="px-3 py-2 font-medium">
                {q.client_name || '(sin nombre)'}
              </td>
              <td className="px-3 py-2">{q.title}</td>
              <td className="px-3 py-2">
                <span className="inline-block px-2 py-0.5 rounded text-xs bg-muted">
                  {STATUS_LABEL[q.status]}
                </span>
              </td>
              <td className="px-3 py-2 text-right font-mono">
                {q.currency} {Number(q.total).toLocaleString('es-MX', { minimumFractionDigits: 2 })}
              </td>
              <td className="px-3 py-2 text-xs text-muted-foreground">
                {q.valid_until || '—'}
              </td>
              <td className="px-3 py-2">
                <a
                  href={quotesApi.pdfUrl(q.id)}
                  target="_blank"
                  rel="noreferrer"
                  className="text-primary hover:underline text-xs"
                  onClick={(e) => e.stopPropagation()}
                >
                  PDF
                </a>
              </td>
              <td className="px-3 py-2">
                {q.status !== 'draft' ? (
                  <code className="text-xs text-muted-foreground">{q.public_url}</code>
                ) : (
                  <span className="text-xs text-muted-foreground">—</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="px-3 py-2 text-xs text-muted-foreground">
        Total: {data.count} · página {Math.floor(data.offset / data.limit) + 1}
      </div>
    </div>
  );
}
